import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Tag,
  Tabs,
  Card,
  Select,
  InputNumber,
  Descriptions,
  Statistic,
  Row,
  Col,
  Badge,
  Tooltip,
  Empty,
  Upload,
  Progress
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  PlayCircleOutlined,
  UndoOutlined,
  FileTextOutlined,
  MessageOutlined,
  CheckCircleOutlined,
  UploadOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import * as XLSX from 'xlsx';
import {
  testAgentService,
  TestAgentSuite,
  TestAgentTurn,
  ExpectedAnswer,
  TestAgentExecution,
  TestAgentResult
} from '../services/testAgentService';
import { authService } from '../services/authService';
import { useI18n } from '../hooks/useI18n';
import './common.css';

const { TabPane } = Tabs;
const { TextArea } = Input;
const { Option } = Select;

const TestAgentManager: React.FC = () => {
  const { t } = useI18n();
  // 租户管理
  const [tenantCode, setTenantCode] = useState<string>(authService.getCurrentTenant() || 'default');
  // 状态管理
  const [testSuites, setTestSuites] = useState<TestAgentSuite[]>([]);
  const [selectedSuite, setSelectedSuite] = useState<TestAgentSuite | null>(null);
  const [session_turns, setTestAgentTurns] = useState<TestAgentTurn[]>([]);
  const [testExecutions, setTestAgentExecutions] = useState<TestAgentExecution[]>([]);
  const [selectedExecution, setSelectedExecution] = useState<TestAgentExecution | null>(null);
  const [testResults, setTestAgentResults] = useState<TestAgentResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('suites');

  // 模态框状态
  const [suiteModalVisible, setSuiteModalVisible] = useState(false);
  const [conversationModalVisible, setTestAgentTurnModalVisible] = useState(false);
  const [answerModalVisible, setAnswerModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [viewDataModalVisible, setViewDataModalVisible] = useState(false);

  // 编辑状态
  const [editingSuite, setEditingSuite] = useState<TestAgentSuite | null>(null);
  const [editingTestAgentTurn, setEditingTestAgentTurn] = useState<TestAgentTurn | null>(null);
  const [selectedTestAgentTurn, setSelectedTestAgentTurn] = useState<TestAgentTurn | null>(null);
  const [editingAnswer, setEditingAnswer] = useState<ExpectedAnswer | null>(null);

  // CSV导入状态
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState<'idle' | 'processing' | 'completed' | 'error'>('idle');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  // 表单实例
  const [suiteForm] = Form.useForm();
  const [conversationForm] = Form.useForm();
  const [answerForm] = Form.useForm();
  const [importForm] = Form.useForm();

  // 加载数据 - 监听租户变化
  useEffect(() => {
    const currentTenant = authService.getCurrentTenant() || 'default';
    if (currentTenant !== tenantCode) {
      setTenantCode(currentTenant);
      setSelectedSuite(null);
      setTestAgentTurns([]);
    }
  }, []);

  useEffect(() => {
    if (tenantCode) {
      loadTestAgentSuites();
    }
  }, [tenantCode]);

  useEffect(() => {
    if (selectedSuite) {
      loadTestAgentTurns(selectedSuite.id!);
    }
  }, [selectedSuite]);

  // ==================== 数据加载函数 ====================

  const loadTestAgentSuites = async () => {
    try {
      setLoading(true);
      const data = await testAgentService.getTestAgentSuites(tenantCode);
      setTestSuites(data);
    } catch (error: any) {
      message.error(t('testAgent.loadSuitesFailed') + '：' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const loadTestAgentTurns = async (suiteId: number) => {
    try {
      setLoading(true);
      const data = await testAgentService.getTestAgentTurns(tenantCode, suiteId);
      setTestAgentTurns(data);
    } catch (error: any) {
      message.error(t('testAgent.loadConversationsFailed') + '：' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const loadTestAgentExecutions = async (suiteId: number) => {
    try {
      setLoading(true);
      const data = await testAgentService.getTestAgentExecutions(tenantCode, suiteId);
      setTestAgentExecutions(data);
    } catch (error: any) {
      message.error(t('testAgent.loadExecutionsFailed') + '：' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const loadTestAgentResults = async (executionId: number) => {
    try {
      setLoading(true);
      const data = await testAgentService.getTestAgentResults(tenantCode, executionId);
      setTestAgentResults(data);
    } catch (error: any) {
      message.error(t('testAgent.loadResultsFailed') + '：' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  // ==================== 测试套件操作 ====================

  const handleCreateOrUpdateSuite = async (values: any) => {
    try {
      if (editingSuite) {
        await testAgentService.updateTestAgentSuite(tenantCode, editingSuite.id!, values);
        message.success(t('testAgent.suiteUpdateSuccess'));
      } else {
        await testAgentService.createTestAgentSuite(tenantCode, values);
        message.success(t('testAgent.suiteCreateSuccess'));
      }
      setSuiteModalVisible(false);
      suiteForm.resetFields();
      setEditingSuite(null);
      loadTestAgentSuites();
    } catch (error: any) {
      message.error(t('testAgent.operationFailed') + '：' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteSuite = async (id: number) => {
    try {
      await testAgentService.deleteTestAgentSuite(tenantCode, id);
      message.success(t('testAgent.suiteDeleteSuccess'));
      loadTestAgentSuites();
    } catch (error: any) {
      message.error(t('testAgent.deleteFailed') + '：' + (error.response?.data?.error || error.message));
    }
  };

  const openSuiteModal = (suite?: TestAgentSuite) => {
    if (suite) {
      setEditingSuite(suite);
      suiteForm.setFieldsValue(suite);
    } else {
      setEditingSuite(null);
      suiteForm.resetFields();
    }
    setSuiteModalVisible(true);
  };

  // ==================== 对话操作 ====================

  const handleCreateOrUpdateTestAgentTurn = async (values: any) => {
    try {
      if (!selectedSuite) {
        message.error(t('testAgent.selectSuiteFirst'));
        return;
      }

      const conversationData = {
        ...values,
        suite_id: selectedSuite.id
      };

      if (editingTestAgentTurn) {
        await testAgentService.updateTestAgentTurn(tenantCode, editingTestAgentTurn.id!, conversationData);
        message.success(t('testAgent.conversationUpdateSuccess'));
      } else {
        await testAgentService.createTestAgentTurn(tenantCode, conversationData);
        message.success(t('testAgent.conversationCreateSuccess'));
      }

      setTestAgentTurnModalVisible(false);
      conversationForm.resetFields();
      setEditingTestAgentTurn(null);
      loadTestAgentTurns(selectedSuite.id!);
    } catch (error: any) {
      message.error(t('testAgent.operationFailed') + '：' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteTestAgentTurn = async (id: number) => {
    try {
      await testAgentService.deleteTestAgentTurn(tenantCode, id);
      message.success(t('testAgent.conversationDeleteSuccess'));
      if (selectedSuite) {
        loadTestAgentTurns(selectedSuite.id!);
      }
    } catch (error: any) {
      message.error(t('testAgent.deleteFailed') + '：' + (error.response?.data?.error || error.message));
    }
  };

  const openTestAgentTurnModal = (conversation?: TestAgentTurn) => {
    if (conversation) {
      setEditingTestAgentTurn(conversation);
      conversationForm.setFieldsValue(conversation);
    } else {
      setEditingTestAgentTurn(null);
      conversationForm.resetFields();
    }
    setTestAgentTurnModalVisible(true);
  };

  // ==================== 预期答案操作 ====================

  const handleAddExpectedAnswer = async (values: any) => {
    try {
      if (!selectedTestAgentTurn) {
        message.error(t('testAgent.selectConversationFirst'));
        return;
      }

      // 解析关键词配置
      let comparison_keywords: string | undefined = undefined;
      if (values.comparison_method === 'keyword' && values.keywords) {
        comparison_keywords = JSON.stringify({
          keywords: values.keywords.split(',').map((k: string) => k.trim()),
          matchAll: values.matchAll || false
        });
      }

      const answerData: Omit<ExpectedAnswer, 'id' | 'created_at'> = {
        turn_id: selectedTestAgentTurn.id!,
        step_order: values.step_order,
        content: values.content,
        comparison_method: values.comparison_method,
        comparison_keywords,
        comparison_custom: values.comparison_custom || undefined
      };

      if (editingAnswer) {
        await testAgentService.updateExpectedAnswer(tenantCode, editingAnswer.id!, answerData);
        message.success(t('testAgent.answerUpdateSuccess'));
      } else {
        await testAgentService.createExpectedAnswer(tenantCode, answerData);
        message.success(t('testAgent.answerCreateSuccess'));
      }
      
      setAnswerModalVisible(false);
      answerForm.resetFields();
      setEditingAnswer(null);
      
      // 刷新对话列表以显示最新的预期答案
      if (selectedSuite) {
        loadTestAgentTurns(selectedSuite.id!);
      }
    } catch (error: any) {
      message.error(t('testAgent.operationFailed') + '：' + (error.response?.data?.error || error.message));
    }
  };

  const openAnswerModal = (conversation: TestAgentTurn, answer?: ExpectedAnswer) => {
    setSelectedTestAgentTurn(conversation);
    
    if (answer) {
      setEditingAnswer(answer);
      // 解析关键词配置用于编辑
      let keywords = '';
      let matchAll = false;
      if (answer.comparison_keywords) {
        try {
          const config = JSON.parse(answer.comparison_keywords);
          keywords = config.keywords.join(', ');
          matchAll = config.matchAll;
        } catch (e) {
          console.error('解析关键词配置失败', e);
        }
      }
      answerForm.setFieldsValue({
        step_order: answer.step_order,
        content: answer.content,
        comparison_method: answer.comparison_method,
        keywords,
        matchAll,
        comparison_custom: answer.comparison_custom
      });
    } else {
      setEditingAnswer(null);
      answerForm.resetFields();
      // 设置默认的步骤序号为当前已有答案数量+1
      const existingAnswersCount = conversation.expected_answers?.length || 0;
      answerForm.setFieldsValue({
        step_order: existingAnswersCount + 1,
        comparison_method: 'exact'
      });
    }
    setAnswerModalVisible(true);
  };

  const handleDeleteExpectedAnswer = async (answerId: number) => {
    try {
      await testAgentService.deleteExpectedAnswer(tenantCode, answerId);
      message.success(t('testAgent.answerDeleteSuccess'));
      if (selectedSuite) {
        loadTestAgentTurns(selectedSuite.id!);
      }
    } catch (error: any) {
      message.error(t('testAgent.deleteFailed') + '：' + (error.response?.data?.error || error.message));
    }
  };

  // ==================== 测试执行功能 ====================

  const handleRunTestSuite = async () => {
    if (!selectedSuite) {
      message.error(t('testAgent.selectSuiteFirst'));
      return;
    }

    try {
      setLoading(true);
      message.loading({ content: t('testAgent.testExecuting'), key: 'runTest', duration: 0 });

      // 执行测试套件
      const result = await testAgentService.runTestAgentSuite(tenantCode, selectedSuite.id!);

      message.success({ 
        content: t('testAgent.testExecuteComplete'), 
        key: 'runTest',
        duration: 2 
      });

      // 刷新执行记录列表
      await loadTestAgentExecutions(selectedSuite.id!);

      // 自动显示最新的执行结果
      if (result.execution.id) {
        setSelectedExecution(result.execution as TestAgentExecution);
        setTestAgentResults(result.results);
      }

      // 显示统计信息
      Modal.success({
        title: t('testAgent.testExecuteCompleteTitle'),
        content: (
          <div>
            <p>{t('testAgent.totalResults')}: {result.statistics.total_results}</p>
            <p>{t('testAgent.matchedCount')}: {result.statistics.matched_count}</p>
            <p>{t('testAgent.mismatchedCount')}: {result.statistics.mismatched_count}</p>
            <p>{t('testAgent.partialCount')}: {result.statistics.partial_count}</p>
            <p>{t('testAgent.averageScore')}: {result.statistics.average_score?.toFixed(2)}</p>
          </div>
        ),
      });

    } catch (error: any) {
      message.error({ 
        content: t('testAgent.testExecuteFailed') + '：' + (error.response?.data?.error || error.message),
        key: 'runTest',
        duration: 5
      });
      console.error('Test execution error:', error);
    } finally {
      setLoading(false);
    }
  };

  // ==================== CSV导入功能 ====================

  // 更健壮的CSV行解析函数，正确处理空字段和引号
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];
      
      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // 转义的引号
          current += '"';
          i++; // 跳过下一个引号
        } else {
          // 切换引号状态
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        // 字段分隔符
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    // 添加最后一个字段
    result.push(current.trim());
    
    return result;
  };

  const parseCSVFile = (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const lines = text.split('\n').filter(line => line.trim());
          if (lines.length < 2) {
            reject(new Error(t('testAgent.csvFormatError')));
            return;
          }
          
          // 解析表头
          const headers = parseCSVLine(lines[0]);
          
          // 解析数据行
          const data = lines.slice(1).map(line => {
            const values = parseCSVLine(line);
            const row: any = {};
            headers.forEach((header, index) => {
              // 保留原始值，包括空字符串
              row[header] = values[index] !== undefined ? values[index] : '';
            });
            return row;
          });
          
          resolve(data);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error(t('testAgent.fileReadFailed')));
      reader.readAsText(file);
    });
  };

  const parseExcelFile = (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          
          // 获取第一个工作表
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          // 将工作表转换为JSON数组
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
            raw: false,  // 将所有值转换为字符串
            defval: ''   // 空单元格的默认值
          });
          
          if (jsonData.length === 0) {
            reject(new Error(t('testAgent.excelFormatError')));
            return;
          }
          
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error(t('testAgent.fileReadFailed')));
      reader.readAsBinaryString(file);
    });
  };

  const handleImportCSV = async (values: any) => {
    if (!uploadedFile) {
      message.error(t('testAgent.uploadFileFirst'));
      return;
    }

    try {
      setImportStatus('processing');
      setImportProgress(0);

      // 解析CSV文件
      const data = await parseCSVFile(uploadedFile);
      setImportProgress(20);

      // 过滤数据：只保留sender_type为USER或ROBOT，且msg_content不为空的记录
      const filteredData = data.filter(row => 
        (row.SENDER_TYPE === 'USER' || row.SENDER_TYPE === 'ROBOT') && 
        row.MSG_CONTENT && 
        row.MSG_CONTENT.trim() !== ''
      );
      setImportProgress(30);

      if (filteredData.length === 0) {
        throw new Error(t('testAgent.noValidData'));
      }

      // 使用文件名作为测试套件名称
      const suiteName = values.suite_name || uploadedFile.name.replace(/\.(csv|xlsx?)$/i, '');
      
      // 创建测试套件
      const suite = await testAgentService.createTestAgentSuite(tenantCode, {
        name: suiteName,
        description: values.description || t('testAgent.importedFrom', { filename: uploadedFile.name })
      });
      setImportProgress(40);

      // 处理对话数据
      const conversationsMap = new Map<string, any>();
      let currentTestAgentTurnId = filteredData[0].ID;
      let conversationSequence = 1;

      // 清理开头非USER发起的对话
      let firstUserIndex = filteredData.findIndex(row => row.SENDER_TYPE === 'USER');
      if (firstUserIndex === -1) {
        throw new Error(t('testAgent.noUserConversation'));
      }
      const validData = filteredData.slice(firstUserIndex);

      // 按顺序分组对话：每轮对话必须由USER发起，后续的ROBOT回复归属于最近的USER
      let conversationKey = '';
      let conversationIndex = 0;
      
      for (const row of validData) {
        if (row.SENDER_TYPE === 'USER') {
          // 创建新的对话组
          conversationKey = `conv_${conversationIndex++}_${Date.now()}_${Math.random()}`;
          conversationsMap.set(conversationKey, {
            user: row,
            robots: []
          });
        } else if (row.SENDER_TYPE === 'ROBOT' && conversationKey) {
          // ROBOT回复直接添加到最近的USER对话中（不检查ID）
          const conv = conversationsMap.get(conversationKey);
          if (conv) {
            conv.robots.push(row);
          }
        }
      }

      // 创建对话和预期答案
      let processedCount = 0;
      const totalTestAgentTurns = conversationsMap.size;

      for (const [id, convData] of conversationsMap) {
        // 检查是否有LIKE反馈
        const hasLikeFeedback = convData.robots.some((r: any) => r.FEEDBACK === 'LIKE');
        if (!hasLikeFeedback || !convData.user) {
          continue; // 跳过没有LIKE反馈或没有用户发起的对话
        }

        // 创建对话（使用CSV中的CREATE_TIME作为created_at）
        const conversation = await testAgentService.createTestAgentTurn(tenantCode, {
          suite_id: suite.id!,
          sequence: conversationSequence++,
          question: convData.user.MSG_CONTENT,
          user_name: convData.user.SENDER_NAME,
          user_code: convData.user.SENDER_CODE,
          robot_name: convData.robots[0]?.SENDER_NAME,
          robot_code: convData.robots[0]?.SENDER_CODE,
          created_at: convData.user.CREATE_TIME || undefined
        });

        // 创建预期答案（使用CSV中的CREATE_TIME作为created_at）
        // 过滤掉MSG_CONTENT为空的robot记录，确保step_order连续
        const validRobots = convData.robots.filter((r: any) => 
          r.MSG_CONTENT && r.MSG_CONTENT.trim() !== ''
        );
        
        for (let i = 0; i < validRobots.length; i++) {
          const robot = validRobots[i];
          await testAgentService.createExpectedAnswer(tenantCode, {
            turn_id: conversation.id!,
            step_order: i + 1,
            content: robot.MSG_CONTENT,
            comparison_method: 'exact',
            created_at: robot.CREATE_TIME || undefined
          });
        }

        processedCount++;
        setImportProgress(40 + Math.floor((processedCount / totalTestAgentTurns) * 50));
      }

      setImportProgress(100);
      setImportStatus('completed');
      message.success(t('testAgent.importSuccess', { count: processedCount }));
      
      // 刷新列表
      loadTestAgentSuites();
      
      // 延迟关闭模态框
      setTimeout(() => {
        setImportModalVisible(false);
        importForm.resetFields();
        setUploadedFile(null);
        setImportStatus('idle');
        setImportProgress(0);
      }, 2000);
      
    } catch (error: any) {
      setImportStatus('error');
      message.error(t('testAgent.importFailed') + '：' + (error.response?.data?.error || error.message));
    }
  };

  const handleImportExcel = async (values: any) => {
    if (!uploadedFile) {
      message.error(t('testAgent.uploadFileFirst'));
      return;
    }

    try {
      setImportStatus('processing');
      setImportProgress(0);

      // 解析Excel文件
      const data = await parseExcelFile(uploadedFile);
      setImportProgress(20);

      // 过滤数据：只保留sender_type为USER或ROBOT，且msg_content不为空的记录
      const filteredData = data.filter(row => 
        (row.SENDER_TYPE === 'USER' || row.SENDER_TYPE === 'ROBOT') && 
        row.MSG_CONTENT && 
        row.MSG_CONTENT.trim() !== ''
      );
      setImportProgress(30);

      if (filteredData.length === 0) {
        throw new Error(t('testAgent.noValidData'));
      }

      // 使用文件名作为测试套件名称
      const suiteName = values.suite_name || uploadedFile.name.replace(/\.(csv|xlsx?)$/i, '');
      
      // 创建测试套件
      const suite = await testAgentService.createTestAgentSuite(tenantCode, {
        name: suiteName,
        description: values.description || t('testAgent.importedFrom', { filename: uploadedFile.name })
      });
      setImportProgress(40);

      // 处理对话数据
      const conversationsMap = new Map<string, any>();
      let currentTestAgentTurnId = filteredData[0].ID;
      let conversationSequence = 1;

      // 清理开头非USER发起的对话
      let firstUserIndex = filteredData.findIndex(row => row.SENDER_TYPE === 'USER');
      if (firstUserIndex === -1) {
        throw new Error(t('testAgent.excelNoUserConversation'));
      }
      const validData = filteredData.slice(firstUserIndex);

      // 按顺序分组对话：每轮对话必须由USER发起，后续的ROBOT回复归属于最近的USER
      let conversationKey = '';
      let conversationIndex = 0;
      
      for (const row of validData) {
        if (row.SENDER_TYPE === 'USER') {
          // 创建新的对话组
          conversationKey = `conv_${conversationIndex++}_${Date.now()}_${Math.random()}`;
          conversationsMap.set(conversationKey, {
            user: row,
            robots: []
          });
        } else if (row.SENDER_TYPE === 'ROBOT' && conversationKey) {
          // ROBOT回复直接添加到最近的USER对话中（不检查ID）
          const conv = conversationsMap.get(conversationKey);
          if (conv) {
            conv.robots.push(row);
          }
        }
      }

      // 创建对话和预期答案
      let processedCount = 0;
      const totalTestAgentTurns = conversationsMap.size;

      for (const [id, convData] of conversationsMap) {
        // 检查是否有LIKE反馈
        const hasLikeFeedback = convData.robots.some((r: any) => r.FEEDBACK === 'LIKE');
        if (!hasLikeFeedback || !convData.user) {
          continue; // 跳过没有LIKE反馈或没有用户发起的对话
        }

        // 创建对话（使用Excel中的CREATE_TIME作为created_at）
        const conversation = await testAgentService.createTestAgentTurn(tenantCode, {
          suite_id: suite.id!,
          sequence: conversationSequence++,
          question: convData.user.MSG_CONTENT,
          user_name: convData.user.SENDER_NAME,
          user_code: convData.user.SENDER_CODE,
          robot_name: convData.robots[0]?.SENDER_NAME,
          robot_code: convData.robots[0]?.SENDER_CODE,
          created_at: convData.user.CREATE_TIME || undefined
        });

        // 创建预期答案（使用Excel中的CREATE_TIME作为created_at）
        // 过滤掉MSG_CONTENT为空的robot记录，确保step_order连续
        const validRobots = convData.robots.filter((r: any) => 
          r.MSG_CONTENT && r.MSG_CONTENT.trim() !== ''
        );
        
        for (let i = 0; i < validRobots.length; i++) {
          const robot = validRobots[i];
          await testAgentService.createExpectedAnswer(tenantCode, {
            turn_id: conversation.id!,
            step_order: i + 1,
            content: robot.MSG_CONTENT,
            comparison_method: 'exact',
            created_at: robot.CREATE_TIME || undefined
          });
        }

        processedCount++;
        setImportProgress(40 + Math.floor((processedCount / totalTestAgentTurns) * 50));
      }

      setImportProgress(100);
      setImportStatus('completed');
      message.success(t('testAgent.importSuccess', { count: processedCount }));
      
      // 刷新列表
      loadTestAgentSuites();
      
      // 延迟关闭模态框
      setTimeout(() => {
        setImportModalVisible(false);
        importForm.resetFields();
        setUploadedFile(null);
        setImportStatus('idle');
        setImportProgress(0);
      }, 2000);
      
    } catch (error: any) {
      setImportStatus('error');
      message.error(t('testAgent.importFailed') + '：' + (error.response?.data?.error || error.message));
    }
  };

  const handleUploadChange = (info: any) => {
    const file = info.file.originFileObj || info.file;
    setUploadedFile(file);
  };

  // ==================== 查看详情 ====================

  const [detailSuite, setDetailSuite] = useState<TestAgentSuite & { session_turns?: TestAgentTurn[] } | null>(null);

  const showSuiteDetails = async (suite: TestAgentSuite) => {
    try {
      setLoading(true);
      const details = await testAgentService.getTestAgentSuiteDetails(tenantCode, suite.id!);
      setDetailSuite(details);
      setDetailModalVisible(true);
    } catch (error: any) {
      message.error(t('testAgent.detailsLoadFailed') + '：' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  // ==================== 表格列定义 ====================

  const suiteColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: t('testAgent.suiteName'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
      ellipsis: true,
    },
    {
      title: t('testAgent.description'),
      dataIndex: 'description',
      key: 'description',
      width: 300,
      ellipsis: true,
    },
    {
      title: t('testAgent.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text: string) => text ? new Date(text).toLocaleString('zh-CN') : '-',
    },
    {
      title: t('testAgent.actions'),
      key: 'actions',
      width: 400,
      render: (record: TestAgentSuite) => (
        <Space>
          <Tooltip title={t('testAgent.tooltipViewDetails')}>
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => showSuiteDetails(record)}
            >
              {t('testAgent.details')}
            </Button>
          </Tooltip>
          <Tooltip title={t('testAgent.tooltipManageConversations')}>
            <Button
              size="small"
              icon={<MessageOutlined />}
              onClick={() => {
                setSelectedSuite(record);
                setTestAgentTurns([]); // 清空旧的对话数据
                setActiveTab('conversations');
              }}
            />
          </Tooltip>
          <Tooltip title={t('testAgent.tooltipTestReplay')}>
            <Button
              size="small"
              icon={<PlayCircleOutlined />}
              onClick={() => {
                setSelectedSuite(record);
                setTestAgentExecutions([]); // 清空旧的执行记录
                setSelectedExecution(null); // 清空选中的执行记录
                setTestAgentResults([]); // 清空测试结果
                loadTestAgentExecutions(record.id!);
                setActiveTab('executions');
              }}
            />
          </Tooltip>
          <Tooltip title={t('testAgent.tooltipEdit')}>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openSuiteModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title={t('testAgent.deleteSuite')}
            description={t('testAgent.deleteSuiteDesc')}
            onConfirm={() => handleDeleteSuite(record.id!)}
            okText={t('common.ok')}
            cancelText={t('common.cancel')}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const conversationColumns = [
    {
      title: t('testAgent.sequence'),
      dataIndex: 'sequence',
      key: 'sequence',
      width: 80,
      sorter: (a: TestAgentTurn, b: TestAgentTurn) => a.sequence - b.sequence,
    },
    {
      title: t('testAgent.user'),
      dataIndex: 'user_name',
      key: 'user_name',
      width: 100,
      render: (text: string) => text || '-',
    },
    {
      title: t('testAgent.robot'),
      dataIndex: 'robot_name',
      key: 'robot_name',
      width: 100,
      render: (text: string) => text || '-',
    },
    {
      title: t('testAgent.question'),
      dataIndex: 'question',
      key: 'question',
      width: 400,
      ellipsis: true,
    },
    {
      title: t('testAgent.expectedAnswersCount'),
      key: 'answers_count',
      width: 120,
      render: (record: TestAgentTurn) => (
        <Badge count={record.expected_answers?.length || 0} showZero color="blue" />
      ),
    },
    {
      title: t('testAgent.actions'),
      key: 'actions',
      width: 280,
      render: (record: TestAgentTurn) => (
        <Space>
          <Button
            size="small"
            icon={<PlusOutlined />}
            onClick={() => openAnswerModal(record)}
          >
            {t('testAgent.addAnswer')}
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openTestAgentTurnModal(record)}
          />
          <Popconfirm
            title={t('testAgent.deleteConversation')}
            onConfirm={() => handleDeleteTestAgentTurn(record.id!)}
            okText={t('common.ok')}
            cancelText={t('common.cancel')}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 测试执行记录列定义
  const executionColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: t('testAgent.conversationId'),
      dataIndex: 'conversation_id',
      key: 'conversation_id',
      width: 150,
      render: (text: string) => text || '-',
    },
    {
      title: t('testAgent.matchStatus'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const statusMap: { [key: string]: { color: string; text: string } } = {
          running: { color: 'processing', text: t('testAgent.statusRunning') },
          completed: { color: 'success', text: t('testAgent.statusCompleted') },
          failed: { color: 'error', text: t('testAgent.statusFailed') },
        };
        const config = statusMap[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: t('testAgent.startTime'),
      dataIndex: 'started_at',
      key: 'started_at',
      width: 180,
      render: (text: string) => text ? new Date(text).toLocaleString('zh-CN') : '-',
    },
    {
      title: t('testAgent.completedTime'),
      dataIndex: 'completed_at',
      key: 'completed_at',
      width: 180,
      render: (text: string) => text ? new Date(text).toLocaleString('zh-CN') : '-',
    },
    {
      title: t('testAgent.actions'),
      key: 'actions',
      width: 120,
      render: (record: TestAgentExecution) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={() => {
            setSelectedExecution(record);
            loadTestAgentResults(record.id!);
          }}
        >
          {t('testAgent.viewResults')}
        </Button>
      ),
    },
  ];

  // 测试结果列定义
  const resultColumns = [
    {
      title: t('testAgent.sequence'),
      dataIndex: 'sequence',
      key: 'sequence',
      width: 80,
    },
    {
      title: t('testAgent.question'),
      dataIndex: 'question',
      key: 'question',
      width: 300,
      ellipsis: true,
    },
    {
      title: t('testAgent.step'),
      dataIndex: 'step_order',
      key: 'step_order',
      width: 80,
    },
    {
      title: t('testAgent.matchStatus'),
      dataIndex: 'match_status',
      key: 'match_status',
      width: 120,
      render: (status: string) => {
        const statusMap: { [key: string]: { color: string; text: string } } = {
          matched: { color: 'success', text: t('testAgent.matchStatusMatched') },
          mismatched: { color: 'error', text: t('testAgent.matchStatusMismatched') },
          partial: { color: 'warning', text: t('testAgent.matchStatusPartial') },
        };
        const config = statusMap[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: t('testAgent.matchScore'),
      dataIndex: 'match_score',
      key: 'match_score',
      width: 100,
      render: (score: number) => score !== null && score !== undefined ? `${score.toFixed(1)}%` : '-',
    },
    {
      title: t('testAgent.comparisonMethodLabel'),
      dataIndex: 'comparison_method',
      key: 'comparison_method',
      width: 100,
      render: (method: string) => {
        const methodMap: { [key: string]: string } = {
          exact: t('testAgent.comparisonExact'),
          keyword: t('testAgent.comparisonKeyword'),
          custom: t('testAgent.comparisonCustom'),
        };
        return methodMap[method] || method;
      },
    },
  ];

  // ==================== 渲染函数 ====================

  // 渲染测试结果的详细对比
  const renderTestAgentResultDetails = (result: TestAgentResult) => {
    return (
      <div style={{ padding: '16px' }}>
        <Row gutter={16}>
          <Col span={12}>
            <Card size="small" title={t('testAgent.expectedAnswer')} style={{ height: '100%' }}>
              <div className="markdown-content">
                <ReactMarkdown>{result.expected_content || t('testAgent.noExpectedContent')}</ReactMarkdown>
              </div>
            </Card>
          </Col>
          <Col span={12}>
            <Card size="small" title={t('testAgent.actualAnswer')} style={{ height: '100%' }}>
              <div className="markdown-content">
                <ReactMarkdown>{result.actual_content || t('testAgent.noActualContent')}</ReactMarkdown>
              </div>
            </Card>
          </Col>
        </Row>
        {result.diff_details && (
          <Card size="small" title={t('testAgent.diffDetails')} style={{ marginTop: 16 }}>
            <pre style={{ 
              whiteSpace: 'pre-wrap', 
              wordWrap: 'break-word',
              backgroundColor: '#f6f8fa',
              padding: 12,
              borderRadius: 4
            }}>
              {result.diff_details}
            </pre>
          </Card>
        )}
      </div>
    );
  };

  // 渲染对话的预期答案列表
  const renderExpectedAnswers = (conversation: TestAgentTurn) => {
    const answers = conversation.expected_answers;
    if (!answers || answers.length === 0) {
      return <Empty description={t('testAgent.noExpectedAnswers')} image={Empty.PRESENTED_IMAGE_SIMPLE} />;
    }

    return (
      <div style={{ marginTop: 8 }}>
        {answers.map((answer, index) => (
          <Card
            key={answer.id}
            size="small"
            style={{ marginBottom: 8 }}
            title={`${t('testAgent.step')} ${answer.step_order}`}
            extra={
              <Space>
                <Tag color={
                  answer.comparison_method === 'exact' ? 'blue' :
                  answer.comparison_method === 'keyword' ? 'green' : 'orange'
                }>
                  {answer.comparison_method === 'exact' ? t('testAgent.comparisonExact') :
                   answer.comparison_method === 'keyword' ? t('testAgent.comparisonKeyword') : t('testAgent.comparisonCustom')}
                </Tag>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => openAnswerModal(conversation, answer)}
                >
                  {t('common.edit')}
                </Button>
                <Popconfirm
                  title={t('testAgent.deleteExpectedAnswer')}
                  onConfirm={() => handleDeleteExpectedAnswer(answer.id!)}
                  okText={t('common.ok')}
                  cancelText={t('common.cancel')}
                >
                  <Button size="small" danger icon={<DeleteOutlined />}>
                    {t('common.delete')}
                  </Button>
                </Popconfirm>
              </Space>
            }
          >
            <div>
              <strong>{t('testAgent.expectedContentLabel')}：</strong>
              <div style={{ 
                marginTop: 8, 
                padding: '8px 12px', 
                backgroundColor: '#f9f9f9',
                borderRadius: 4,
                border: '1px solid #e8e8e8'
              }} className="markdown-content">
                <ReactMarkdown>{answer.content}</ReactMarkdown>
              </div>
            </div>
            {answer.comparison_keywords && (
              <div style={{ marginTop: 8 }}>
                <strong>{t('testAgent.keywordConfig')}：</strong>
                <span style={{ fontSize: 12, color: '#666' }}>
                  {JSON.parse(answer.comparison_keywords).keywords.join(', ')}
                </span>
              </div>
            )}
          </Card>
        ))}
      </div>
    );
  };

  // ==================== 主渲染 ====================

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          {/* 测试套件列表 */}
          <TabPane
            tab={
              <span>
                <FileTextOutlined />
                {t('testAgent.suites')}
              </span>
            }
            key="suites"
          >
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Button
                  icon={<UploadOutlined />}
                  onClick={() => setImportModalVisible(true)}
                >
                  {t('testAgent.importCSV')}
                </Button>
                <Button
                  icon={<UploadOutlined />}
                  type="primary"
                  onClick={() => setImportModalVisible(true)}
                >
                  {t('testAgent.importExcel')}
                </Button>
              </Space>
            </div>

            <Table
              columns={suiteColumns}
              dataSource={testSuites}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => t('common.paginationTotal', { from: 1, to: Math.min(10, total), total }),
              }}
            />
          </TabPane>

          {/* 对话管理 */}
          <TabPane
            tab={
              <span>
                <MessageOutlined />
                {t('testAgent.conversations')}
                {selectedSuite && ` (${selectedSuite.name})`}
              </span>
            }
            key="conversations"
            disabled={!selectedSuite}
          >
            {selectedSuite && (
              <>
                <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f6f8fa' }}>
                  <Descriptions column={2} size="small">
                    <Descriptions.Item label={t('testAgent.suiteName')}>{selectedSuite.name}</Descriptions.Item>
                    <Descriptions.Item label={t('testAgent.description')}>{selectedSuite.description || '-'}</Descriptions.Item>
                  </Descriptions>
                </Card>

                <div style={{ marginBottom: 16 }}>
                  <Space>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => openTestAgentTurnModal()}
                    >
                      {t('testAgent.createConversation')}
                    </Button>
                    <Button onClick={() => {
                      setSelectedSuite(null);
                      setTestAgentTurns([]); // 清空对话数据
                      setActiveTab('suites');
                    }}>
                      {t('testAgent.backToSuites')}
                    </Button>
                  </Space>
                </div>

                <Table
                  columns={conversationColumns}
                  dataSource={session_turns}
                  rowKey="id"
                  loading={loading}
                  expandable={{
                    expandedRowRender: (record) => renderExpectedAnswers(record),
                    rowExpandable: (record) => true,
                  }}
                  pagination={{
                    pageSize: 10,
                    showTotal: (total) => t('common.paginationTotal', { from: 1, to: Math.min(10, total), total }),
                  }}
                />
              </>
            )}
          </TabPane>

          {/* 对话重放 */}
          <TabPane
            tab={
              <span>
                <PlayCircleOutlined />
                {t('testAgent.executions')}
                {selectedSuite && ` (${selectedSuite.name})`}
              </span>
            }
            key="executions"
            disabled={!selectedSuite}
          >
            {selectedSuite && (
              <>
                <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f6f8fa' }}>
                  <Descriptions column={2} size="small">
                    <Descriptions.Item label={t('testAgent.suiteName')}>{selectedSuite.name}</Descriptions.Item>
                    <Descriptions.Item label={t('testAgent.description')}>{selectedSuite.description || '-'}</Descriptions.Item>
                  </Descriptions>
                </Card>

                {!selectedExecution ? (
                  <>
                    <div style={{ marginBottom: 16 }}>
                      <Space>
                        <Button 
                          type="primary"
                          icon={<PlayCircleOutlined />}
                          loading={loading}
                          onClick={handleRunTestSuite}
                        >
                          {t('testAgent.startReplay')}
                        </Button>
                        <Button onClick={() => {
                          setSelectedSuite(null);
                          setTestAgentExecutions([]); // 清空执行记录
                          setSelectedExecution(null);
                          setTestAgentResults([]);
                          setActiveTab('suites');
                        }}>
                          {t('testAgent.backToSuites')}
                        </Button>
                      </Space>
                    </div>

                    <Table
                      columns={executionColumns}
                      dataSource={testExecutions}
                      rowKey="id"
                      loading={loading}
                      pagination={{
                        pageSize: 10,
                        showTotal: (total) => t('common.paginationTotal', { from: 1, to: Math.min(10, total), total }),
                      }}
                    />
                  </>
                ) : (
                  <>
                    <div style={{ marginBottom: 16 }}>
                      <Space>
                        <Button
                          icon={<UndoOutlined />}
                          onClick={() => {
                            setSelectedExecution(null);
                            setTestAgentResults([]);
                          }}
                        >
                          {t('testAgent.backToExecutions')}
                        </Button>
                      </Space>
                    </div>

                    <Card size="small" style={{ marginBottom: 16 }}>
                      <Descriptions column={3} size="small" bordered>
                        <Descriptions.Item label={t('testAgent.executionId')}>{selectedExecution.id}</Descriptions.Item>
                        <Descriptions.Item label={t('testAgent.conversationId')}>{selectedExecution.conversation_id || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('testAgent.matchStatus')}>
                          {selectedExecution.status === 'completed' && <Tag color="success">{t('testAgent.statusCompleted')}</Tag>}
                          {selectedExecution.status === 'running' && <Tag color="processing">{t('testAgent.statusRunning')}</Tag>}
                          {selectedExecution.status === 'failed' && <Tag color="error">{t('testAgent.statusFailed')}</Tag>}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('testAgent.startTime')}>
                          {selectedExecution.started_at ? new Date(selectedExecution.started_at).toLocaleString('zh-CN') : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('testAgent.completedTime')}>
                          {selectedExecution.completed_at ? new Date(selectedExecution.completed_at).toLocaleString('zh-CN') : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('testAgent.matchStats')}>
                          <Space>
                            <Tag color="success">{t('testAgent.matched')}: {testResults.filter(r => r.match_status === 'matched').length}</Tag>
                            <Tag color="error">{t('testAgent.mismatched')}: {testResults.filter(r => r.match_status === 'mismatched').length}</Tag>
                            <Tag color="warning">{t('testAgent.partial')}: {testResults.filter(r => r.match_status === 'partial').length}</Tag>
                          </Space>
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>

                    <Table
                      columns={resultColumns}
                      dataSource={testResults}
                      rowKey="id"
                      loading={loading}
                      expandable={{
                        expandedRowRender: (record) => renderTestAgentResultDetails(record),
                        rowExpandable: (record) => true,
                      }}
                      pagination={{
                        pageSize: 10,
                        showTotal: (total) => t('common.paginationTotal', { from: 1, to: Math.min(10, total), total }),
                      }}
                    />
                  </>
                )}
              </>
            )}
          </TabPane>
        </Tabs>
      </Card>

      {/* 创建/编辑测试套件模态框 */}
      <Modal
        title={editingSuite ? t('testAgent.editSuite') : t('testAgent.createSuite')}
        open={suiteModalVisible}
        onOk={() => suiteForm.submit()}
        onCancel={() => {
          setSuiteModalVisible(false);
          suiteForm.resetFields();
          setEditingSuite(null);
        }}
        width={600}
      >
        <Form
          form={suiteForm}
          layout="vertical"
          onFinish={handleCreateOrUpdateSuite}
        >
          <Form.Item
            name="name"
            label={t('testAgent.suiteName')}
            rules={[{ required: true, message: t('testAgent.enterSuiteName') }]}
          >
            <Input placeholder={t('testAgent.suiteNamePlaceholder')} />
          </Form.Item>

          <Form.Item name="description" label={t('testAgent.description')}>
            <TextArea rows={4} placeholder={t('testAgent.descriptionPlaceholder')} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 创建/编辑对话模态框 */}
      <Modal
        title={editingTestAgentTurn ? t('testAgent.editConversation') : t('testAgent.createConversation')}
        open={conversationModalVisible}
        onOk={() => conversationForm.submit()}
        onCancel={() => {
          setTestAgentTurnModalVisible(false);
          conversationForm.resetFields();
          setEditingTestAgentTurn(null);
        }}
        width={700}
      >
        <Form
          form={conversationForm}
          layout="vertical"
          onFinish={handleCreateOrUpdateTestAgentTurn}
        >
          <Form.Item
            name="sequence"
            label={t('testAgent.conversationSequence')}
            rules={[{ required: true, message: t('testAgent.enterSequence') }]}
            tooltip={t('testAgent.sequenceTooltip')}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="question"
            label={t('testAgent.questionContent')}
            rules={[{ required: true, message: t('testAgent.enterQuestion') }]}
          >
            <TextArea rows={4} placeholder={t('testAgent.questionPlaceholder')} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="user_name" label={t('testAgent.userName')}>
                <Input placeholder={t('testAgent.userNamePlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="user_code" label={t('testAgent.userCode')}>
                <Input placeholder={t('testAgent.userCodePlaceholder')} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="robot_name" label={t('testAgent.robotName')}>
                <Input placeholder={t('testAgent.robotNamePlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="robot_code" label={t('testAgent.robotCode')}>
                <Input placeholder={t('testAgent.robotCodePlaceholder')} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* 添加/编辑预期答案模态框 */}
      <Modal
        title={editingAnswer ? t('testAgent.editExpectedAnswer') : t('testAgent.addExpectedAnswer')}
        open={answerModalVisible}
        onOk={() => answerForm.submit()}
        onCancel={() => {
          setAnswerModalVisible(false);
          answerForm.resetFields();
          setSelectedTestAgentTurn(null);
          setEditingAnswer(null);
        }}
        width={700}
      >
        <Form
          form={answerForm}
          layout="vertical"
          onFinish={handleAddExpectedAnswer}
        >
          <Form.Item
            name="step_order"
            label={t('testAgent.stepOrder')}
            rules={[{ required: true, message: t('testAgent.enterStepOrder') }]}
            tooltip={t('testAgent.stepOrderTooltip')}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="content"
            label={t('testAgent.expectedContent')}
            rules={[{ required: true, message: t('testAgent.enterExpectedContent') }]}
          >
            <TextArea rows={4} placeholder={t('testAgent.expectedContentPlaceholder')} />
          </Form.Item>

          <Form.Item
            name="comparison_method"
            label={t('testAgent.comparisonMethod')}
            rules={[{ required: true, message: t('testAgent.selectComparisonMethod') }]}
          >
            <Select>
              <Option value="exact">{t('testAgent.methodExact')}</Option>
              <Option value="keyword">{t('testAgent.methodKeyword')}</Option>
              <Option value="custom">{t('testAgent.methodCustom')}</Option>
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.comparison_method !== currentValues.comparison_method}
          >
            {({ getFieldValue }) => {
              const method = getFieldValue('comparison_method');
              
              if (method === 'keyword') {
                return (
                  <>
                    <Form.Item
                      name="keywords"
                      label={t('testAgent.keywords')}
                      rules={[{ required: true, message: t('testAgent.enterKeywords') }]}
                      tooltip={t('testAgent.keywordsTooltip')}
                    >
                      <Input placeholder={t('testAgent.keywordsPlaceholder')} />
                    </Form.Item>

                    <Form.Item name="matchAll" label={t('testAgent.matchMode')} valuePropName="checked">
                      <Select defaultValue={false}>
                        <Option value={false}>{t('testAgent.matchAny')}</Option>
                        <Option value={true}>{t('testAgent.matchAll')}</Option>
                      </Select>
                    </Form.Item>
                  </>
                );
              }

              if (method === 'custom') {
                return (
                  <Form.Item
                    name="comparison_custom"
                    label={t('testAgent.customConfig')}
                    rules={[{ required: true, message: t('testAgent.enterCustomConfig') }]}
                  >
                    <TextArea rows={3} placeholder={t('testAgent.customConfigPlaceholder')} />
                  </Form.Item>
                );
              }

              return null;
            }}
          </Form.Item>
        </Form>
      </Modal>

      {/* CSV/Excel导入模态框 */}
      <Modal
        title={t('testAgent.importCSVExcel')}
        open={importModalVisible}
        onOk={() => importForm.submit()}
        onCancel={() => {
          setImportModalVisible(false);
          importForm.resetFields();
          setUploadedFile(null);
          setImportStatus('idle');
          setImportProgress(0);
        }}
        width={700}
        okButtonProps={{ 
          disabled: !uploadedFile || importStatus === 'processing',
          loading: importStatus === 'processing'
        }}
      >
        <Form
          form={importForm}
          layout="vertical"
          onFinish={(values) => {
            // 根据文件类型调用不同的处理函数
            if (uploadedFile) {
              const fileName = uploadedFile.name.toLowerCase();
              if (fileName.endsWith('.csv')) {
                handleImportCSV(values);
              } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
                handleImportExcel(values);
              } else {
                message.error(t('testAgent.unsupportedFileFormat'));
              }
            }
          }}
        >
          <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f6f8fa' }}>
            <p style={{ margin: 0, fontSize: 12, color: '#666' }}>
              <strong>{t('testAgent.fileFormatRequirements')}：</strong><br/>
              1. {t('testAgent.formatDesc1')}<br/>
              2. {t('testAgent.formatDesc2')}<br/>
              3. {t('testAgent.formatDesc3')}<br/>
              4. {t('testAgent.formatDesc4')}<br/>
              5. {t('testAgent.formatDesc5')}<br/>
              6. {t('testAgent.formatDesc6')}
            </p>
          </Card>

          <Form.Item
            name="suite_name"
            label={t('testAgent.suiteNameOptional')}
            tooltip={t('testAgent.suiteNameTooltip')}
          >
            <Input placeholder={t('testAgent.suiteNameImportPlaceholder')} />
          </Form.Item>

          <Form.Item
            name="description"
            label={t('testAgent.suiteDescription')}
          >
            <TextArea rows={3} placeholder={t('testAgent.suiteDescriptionPlaceholder')} />
          </Form.Item>

          <Form.Item
            label={t('testAgent.selectCSVOrExcel')}
            required
          >
            <Upload
              accept=".csv,.xlsx,.xls"
              maxCount={1}
              beforeUpload={() => false}
              onChange={handleUploadChange}
              onRemove={() => setUploadedFile(null)}
            >
              <Button icon={<UploadOutlined />}>{t('testAgent.chooseFile')}</Button>
            </Upload>
            {uploadedFile && (
              <div style={{ marginTop: 8, color: '#52c41a' }}>
                {t('testAgent.fileSelected')}：{uploadedFile.name}
              </div>
            )}
          </Form.Item>

          {importStatus === 'processing' && (
            <Form.Item label={t('testAgent.importProgress')}>
              <Progress percent={importProgress} status="active" />
            </Form.Item>
          )}

          {importStatus === 'completed' && (
            <Form.Item>
              <div style={{ color: '#52c41a', fontSize: 14 }}>
                <CheckCircleOutlined /> {t('testAgent.importCompleted')}
              </div>
            </Form.Item>
          )}

          {importStatus === 'error' && (
            <Form.Item>
              <div style={{ color: '#ff4d4f', fontSize: 14 }}>
                {t('testAgent.importError')}
              </div>
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* 测试套件详情模态框 */}
      <Modal
        title={t('testAgent.suiteDetails')}
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setDetailSuite(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setDetailModalVisible(false);
            setDetailSuite(null);
          }}>
            {t('testAgent.close')}
          </Button>,
          <Button 
            key="manage" 
            type="primary"
            onClick={() => {
              if (detailSuite) {
                setSelectedSuite(detailSuite as TestAgentSuite);
                setActiveTab('conversations');
                setDetailModalVisible(false);
              }
            }}
          >
            {t('testAgent.manageConversations')}
          </Button>
        ]}
        width={900}
      >
        {detailSuite && (
          <div>
            {/* 测试套件基本信息 */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Descriptions title={t('testAgent.basicInfo')} column={2} bordered size="small">
                <Descriptions.Item label={t('testAgent.suiteId')}>{detailSuite.id}</Descriptions.Item>
                <Descriptions.Item label={t('testAgent.suiteName')}>{detailSuite.name}</Descriptions.Item>
                <Descriptions.Item label={t('testAgent.description')} span={2}>
                  {detailSuite.description || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('testAgent.createdAt')}>
                  {detailSuite.created_at ? new Date(detailSuite.created_at).toLocaleString('zh-CN') : '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('testAgent.updatedAt')}>
                  {detailSuite.updated_at ? new Date(detailSuite.updated_at).toLocaleString('zh-CN') : '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* 统计信息 */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic 
                    title={t('testAgent.conversationCount')} 
                    value={detailSuite.session_turns?.length || 0} 
                    prefix={<MessageOutlined />}
                  />
                </Col>
                <Col span={8}>
                  <Statistic 
                    title={t('testAgent.totalExpectedAnswers')} 
                    value={detailSuite.session_turns?.reduce((sum, conv) => sum + (conv.expected_answers?.length || 0), 0) || 0}
                    prefix={<CheckCircleOutlined />}
                  />
                </Col>
                <Col span={8}>
                  <Statistic 
                    title={t('testAgent.avgAnswersPerConversation')} 
                    value={detailSuite.session_turns?.length ? 
                      ((detailSuite.session_turns?.reduce((sum, conv) => sum + (conv.expected_answers?.length || 0), 0) || 0) / detailSuite.session_turns.length).toFixed(1) : 
                      '0'
                    }
                  />
                </Col>
              </Row>
            </Card>

            {/* 对话列表 */}
            <Card size="small" title={t('testAgent.conversationList')}>
              {detailSuite.session_turns && detailSuite.session_turns.length > 0 ? (
                <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                  {detailSuite.session_turns.map((conv: TestAgentTurn) => (
                    <Card 
                      key={conv.id} 
                      size="small" 
                      style={{ marginBottom: 12 }}
                      title={`${t('testAgent.sequence')} ${conv.sequence}: ${conv.question.substring(0, 50)}${conv.question.length > 50 ? '...' : ''}`}
                    >
                      <Descriptions column={2} size="small">
                        <Descriptions.Item label={t('testAgent.user')}>{conv.user_name || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('testAgent.robot')}>{conv.robot_name || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('testAgent.question')} span={2}>
                          {conv.question}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('testAgent.expectedAnswersCount')} span={2}>
                          <Badge count={conv.expected_answers?.length || 0} showZero color="blue" />
                        </Descriptions.Item>
                      </Descriptions>
                      
                      {/* 预期答案列表 */}
                      {conv.expected_answers && conv.expected_answers.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: 12, color: '#666' }}>
                            {t('testAgent.expectedContentLabel')}：
                          </div>
                          {conv.expected_answers.map((answer: ExpectedAnswer) => (
                            <div 
                              key={answer.id} 
                              style={{ 
                                padding: '8px 12px', 
                                marginBottom: 8, 
                                backgroundColor: '#f6f8fa', 
                                borderRadius: 4,
                                fontSize: 12
                              }}
                            >
                              <div style={{ marginBottom: 8 }}>
                                <Tag color={
                                  answer.comparison_method === 'exact' ? 'blue' :
                                  answer.comparison_method === 'keyword' ? 'green' : 'orange'
                                }>
                                  {t('testAgent.step')} {answer.step_order}
                                </Tag>
                                <Tag>
                                  {answer.comparison_method === 'exact' ? t('testAgent.comparisonExact') :
                                   answer.comparison_method === 'keyword' ? t('testAgent.comparisonKeyword') : t('testAgent.comparisonCustom')}
                                </Tag>
                              </div>
                              <div style={{ 
                                padding: '8px', 
                                backgroundColor: '#ffffff',
                                borderRadius: 4,
                                border: '1px solid #e8e8e8'
                              }} className="markdown-content">
                                <ReactMarkdown>{answer.content}</ReactMarkdown>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              ) : (
                <Empty description={t('testAgent.noConversations')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </Card>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TestAgentManager;



